import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Plus, Trash, Pencil, ArrowClockwise, ClockClockwise, Play } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { usePagination } from '../components/TablePagination';
import SearchableSelect from '../components/SearchableSelect';
import { toDDMMYYYY, todayISO } from '../lib/dateUtils';
import { loadCompanies } from '../lib/companies';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyItem = () => ({ description: '', details: '', qty: 1, unit_price: 0, tax_percentage: 0, amount: 0, hsn_sac: '' });
const empty = () => ({
  id: '', name: '', company_id: 'dextra_tech', client_id: '',
  items: [emptyItem()], discount_value: 0, discount_type: 'amount',
  tax_percentage: 0, invoice_taxes: [], place_of_supply: '', currency: 'INR',
  notes: '', terms: '', payment_mode: 'NEFT',
  frequency: 'monthly', next_run: todayISO(), active: true,
});

const FREQ_LABEL = { weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };
const PAYMENT_MODES = ['NEFT', 'IMPS', 'RTGS', 'UPI', 'Cheque', 'Cash', 'Card', 'Other'];

// Indian state codes for Place of Supply (subset — same as InvoicesPage).
const STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra',
  'Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu',
  'Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Other',
];

export const RecurringPage = () => {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(empty());
  const [processing, setProcessing] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API_URL}/api/recurring`, { withCredentials: true }),
        axios.get(`${API_URL}/api/clients`, { withCredentials: true }),
      ]);
      setList(r.data); setClients(c.data);
      const cos = await loadCompanies();
      setCompanies(cos);
      if (cos.length && !cos.find((co) => co.id === form.company_id)) {
        setForm((f) => ({ ...f, company_id: cos[0].id }));
      }
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const companyMap = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c])), [companies]);

  const filteredRecurring = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? list.filter((t) => {
      const cl = clientMap[t.client_id];
      return `${t.name || ''} ${cl?.first_name || ''} ${cl?.last_name || ''} ${cl?.company_name || ''} ${FREQ_LABEL[t.frequency] || ''}`.toLowerCase().includes(q);
    }) : list;
  }, [list, search, clientMap]);
  const { paged: pagedRecurring, controls: recControls, startIdx: recStart } = usePagination(filteredRecurring, { storageKey: 'recurring-page-size', testIdPrefix: 'recurring' });

  const setItem = (i, key, val) => setForm({ ...form, items: form.items.map((it, idx) => idx === i ? { ...it, [key]: val } : it) });

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      discount_value: parseFloat(form.discount_value) || 0,
      tax_percentage: parseFloat(form.tax_percentage) || 0,
      items: form.items.map((it) => ({
        description: it.description, details: it.details || '',
        qty: parseFloat(it.qty) || 0, unit_price: parseFloat(it.unit_price) || 0,
        tax_percentage: parseFloat(it.tax_percentage) || 0, hsn_sac: it.hsn_sac || '', amount: 0,
      })),
      invoice_taxes: (form.invoice_taxes || []).map((t) => ({ id: t.id || '', name: t.name, percentage: parseFloat(t.percentage) || 0 })),
    };
    try {
      if (edit) await axios.put(`${API_URL}/api/recurring/${form.id}`, payload, { withCredentials: true });
      else await axios.post(`${API_URL}/api/recurring`, payload, { withCredentials: true });
      setOpen(false); setForm(empty()); setEdit(false); load();
    } catch (err) { alert('Save failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const onEdit = (t) => { setForm({ ...empty(), ...t, items: t.items?.length ? t.items : [emptyItem()] }); setEdit(true); setOpen(true); };
  const onDel = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await axios.delete(`${API_URL}/api/recurring/${id}`, { withCredentials: true });
    load();
  };

  const processNow = async () => {
    setProcessing(true);
    try {
      const r = await axios.post(`${API_URL}/api/recurring/process-due`, {}, { withCredentials: true });
      alert(`Generated ${r.data.created} invoice(s).`);
      load();
    } catch (err) { alert('Process failed'); }
    finally { setProcessing(false); }
  };

  return (
    <div data-testid="recurring-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--dx-text)' }}>Recurring Invoices</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>Templates that auto-generate invoices on schedule</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={processNow} disabled={processing} data-testid="process-due-btn">
            <Play size={14} className="mr-1" /> {processing ? 'Processing…' : 'Run Due Now'}
          </Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty()); setEdit(false); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-template-btn" className="text-white" style={{ background: 'var(--dx-primary)' }}>
                <Plus size={18} className="mr-1" /> New Template
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{edit ? 'Edit Template' : 'New Recurring Template'}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div><Label>Template Name *</Label><Input required value={form.name} placeholder="ACME Monthly Subscription" onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div>
                    <Label>Company *</Label>
                    <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{companies.map((co) => (<SelectItem key={co.id} value={co.id}>{co.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Client *</Label>
                    <SearchableSelect
                      options={clients.map((c) => {
                        const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—';
                        const company = (c.company_name || '').trim();
                        return {
                          value: c.id,
                          label: company ? `${name} — ${company}` : name,
                          sublabel: c.email || c.contact_no || '',
                          search: `${name} ${company} ${c.email || ''} ${c.contact_no || ''} ${c.gstin || ''}`,
                        };
                      })}
                      value={form.client_id}
                      onChange={(v) => setForm({ ...form, client_id: v })}
                      placeholder="Search client by name, company or email…"
                      testId="recurring-client-select"
                    />
                  </div>
                  <div>
                    <Label>Frequency *</Label>
                    <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(FREQ_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Next Run Date *</Label><Input required type="date" value={form.next_run} onChange={(e) => setForm({ ...form, next_run: e.target.value })} /></div>
                  <div className="flex items-center gap-3">
                    <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                    <Label>Active</Label>
                  </div>
                </div>

                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm">Line Items</h3>
                    <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })}>
                      <Plus size={14} className="mr-1" /> Add row
                    </Button>
                  </div>
                  {(() => {
                    const co = companyMap[form.company_id];
                    const gstEnabled = !!co?.gst_enabled;
                    return (
                      <table className="w-full text-sm">
                        <thead><tr style={{ background: 'var(--dx-bg)' }}>
                          <th className="text-left p-2 text-xs uppercase">Description</th>
                          {gstEnabled && <th className="text-left p-2 text-xs uppercase w-28">HSN/SAC</th>}
                          <th className="text-right p-2 text-xs uppercase w-20">Qty</th>
                          <th className="text-right p-2 text-xs uppercase w-28">Unit Price</th>
                          <th className="text-right p-2 text-xs uppercase w-24">Tax %</th>
                          <th className="w-10"></th>
                        </tr></thead>
                        <tbody>
                          {form.items.map((it, i) => (
                            <tr key={i} className="border-b">
                              <td className="p-1">
                                <Input value={it.description} placeholder="Description" onChange={(e) => setItem(i, 'description', e.target.value)} />
                                <Input className="mt-1 text-xs" value={it.details || ''} placeholder="Details (optional)" onChange={(e) => setItem(i, 'details', e.target.value)} />
                              </td>
                              {gstEnabled && (
                                <td className="p-1"><Input value={it.hsn_sac || ''} placeholder="HSN/SAC" onChange={(e) => setItem(i, 'hsn_sac', e.target.value)} /></td>
                              )}
                              <td className="p-1"><Input className="text-right" type="number" step="0.01" value={it.qty} onChange={(e) => setItem(i, 'qty', e.target.value)} /></td>
                              <td className="p-1"><Input className="text-right" type="number" step="0.01" value={it.unit_price} onChange={(e) => setItem(i, 'unit_price', e.target.value)} /></td>
                              <td className="p-1"><Input className="text-right" type="number" step="0.01" value={it.tax_percentage} onChange={(e) => setItem(i, 'tax_percentage', e.target.value)} /></td>
                              <td className="p-1 text-center">
                                <Button type="button" size="sm" variant="outline" className="text-red-600" onClick={() => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) })}>
                                  <Trash size={14} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>

                {/* GST / Place of Supply / Discount / Invoice-level taxes */}
                {(() => {
                  const co = companyMap[form.company_id];
                  const gstEnabled = !!co?.gst_enabled;
                  return (
                    <div className="border-t pt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                      {gstEnabled && (
                        <div>
                          <Label>Place of Supply</Label>
                          <Select value={form.place_of_supply || '__none'} onValueChange={(v) => setForm({ ...form, place_of_supply: v === '__none' ? '' : v })}>
                            <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">— Not set —</SelectItem>
                              {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-gray-500 mt-1">Required for CGST/SGST vs IGST split on generated invoices.</p>
                        </div>
                      )}
                      <div>
                        <Label>Discount</Label>
                        <div className="flex gap-1">
                          <Input type="number" step="0.01" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="flex-1" />
                          <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                            <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="amount">₹</SelectItem>
                              <SelectItem value="percent">%</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Payment Mode</Label>
                        <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  );
                })()}

                {/* Additional invoice-level taxes (e.g. TDS, surcharge) */}
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm">Extra Invoice-Level Taxes / Charges <span className="text-[10px] font-normal text-gray-500">(optional — e.g. TDS, additional GST, etc.)</span></h3>
                    <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, invoice_taxes: [...(form.invoice_taxes || []), { id: '', name: '', percentage: 0 }] })}>
                      <Plus size={14} className="mr-1" /> Add tax
                    </Button>
                  </div>
                  {(form.invoice_taxes || []).length > 0 && (
                    <table className="w-full text-sm">
                      <thead><tr style={{ background: 'var(--dx-bg)' }}>
                        <th className="text-left p-2 text-xs uppercase">Name</th>
                        <th className="text-right p-2 text-xs uppercase w-32">Percentage</th>
                        <th className="w-10"></th>
                      </tr></thead>
                      <tbody>
                        {form.invoice_taxes.map((t, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-1"><Input value={t.name} placeholder="e.g. TDS 10%" onChange={(e) => setForm({ ...form, invoice_taxes: form.invoice_taxes.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x) })} /></td>
                            <td className="p-1"><Input className="text-right" type="number" step="0.01" value={t.percentage} onChange={(e) => setForm({ ...form, invoice_taxes: form.invoice_taxes.map((x, idx) => idx === i ? { ...x, percentage: e.target.value } : x) })} /></td>
                            <td className="p-1 text-center"><Button type="button" size="sm" variant="outline" className="text-red-600" onClick={() => setForm({ ...form, invoice_taxes: form.invoice_taxes.filter((_, idx) => idx !== i) })}><Trash size={14} /></Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Notes & Terms */}
                <div className="border-t pt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Notes</Label>
                    <textarea
                      className="w-full border rounded h-20 px-2 py-1 text-sm"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Notes to client (visible on invoice)"
                    />
                  </div>
                  <div>
                    <Label>Terms &amp; Conditions</Label>
                    <textarea
                      className="w-full border rounded h-20 px-2 py-1 text-sm"
                      value={form.terms}
                      onChange={(e) => setForm({ ...form, terms: e.target.value })}
                      placeholder="Payment terms / footer note"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" className="text-white" style={{ background: 'var(--dx-primary)' }} data-testid="save-template-btn">{edit ? 'Update' : 'Create Template'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="dx-card">
        <div className="p-3 border-b" style={{ borderColor: 'var(--dx-border)' }}>
          <Input placeholder="Search by template name, client, frequency…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-md" data-testid="recurring-search" />
        </div>
        <Table>
          <TableHeader><TableRow style={{ background: 'var(--dx-bg)' }}>
            <TableHead>#</TableHead>
            <TableHead>NAME</TableHead><TableHead>COMPANY</TableHead><TableHead>CLIENT</TableHead>
            <TableHead>FREQUENCY</TableHead><TableHead>NEXT RUN</TableHead><TableHead>LAST RUN</TableHead>
            <TableHead>ACTIVE</TableHead><TableHead className="text-right">ACTIONS</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {pagedRecurring.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8" style={{ color: 'var(--dx-text2)' }}>
                <ArrowClockwise size={32} className="mx-auto mb-2" style={{ color: 'var(--dx-text3)' }} />
                {list.length === 0 ? 'No recurring templates yet.' : 'No matches.'}
              </TableCell></TableRow>
            ) : pagedRecurring.map((t, idx) => {
              const cl = clientMap[t.client_id];
              const co = companyMap[t.company_id];
              const dueSoon = t.active && t.next_run <= todayISO();
              return (
                <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                  <TableCell className="text-gray-400">{recStart + idx + 1}</TableCell>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell><span className="px-2 py-1 rounded text-[10px] font-bold uppercase" style={{ background: (co?.accent || '#1D9E75') + '22', color: co?.accent || '#1D9E75' }}>{co?.short}</span></TableCell>
                  <TableCell>{cl ? `${cl.first_name} ${cl.last_name || ''}` : '—'}</TableCell>
                  <TableCell>{FREQ_LABEL[t.frequency]}</TableCell>
                  <TableCell>
                    {toDDMMYYYY(t.next_run)}
                    {dueSoon && <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: '#fee2e2', color: '#991b1b' }}>DUE</span>}
                  </TableCell>
                  <TableCell>{t.last_generated ? toDDMMYYYY(t.last_generated) : '—'}</TableCell>
                  <TableCell>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold" style={{ background: t.active ? '#dcfce7' : '#f3f4f6', color: t.active ? '#166534' : '#6b7280' }}>
                      {t.active ? 'YES' : 'NO'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => onEdit(t)}><Pencil size={14} /></Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDel(t.id)}><Trash size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {recControls}
      </Card>
    </div>
  );
};

export default RecurringPage;
